import { useEffect, useState } from 'react';

import * as Sentry from '@sentry/nextjs';

import Button from 'epn-ui/umd/dist/components/Button';
import Dialog from 'epn-ui/umd/dist/components/Dialog';
import Form from 'epn-ui/umd/dist/components/Form';
import Loader from 'epn-ui/umd/dist/components/Loader';
import Notification from 'epn-ui/umd/dist/components/Notification';
import Select, { DefaultOptionType } from 'epn-ui/umd/dist/components/Select';

import debounce from 'lodash/debounce';

import useI18n from '@i18n';

import api from '@api';
import apiHooks, { ICreateUseRequestInfiniteResponse } from '@apiHooks';

import {
    IGetCreativesDataFormatted,
    IGetOffersDataFormatted,
    IGetPlacementsDataFormatted,
} from '@api/types';

import infifniteScroll from '@utils/infiniteScroll';

const { usePlacements } = apiHooks;

interface IModalSelectPlacement {
    onClose: () => void;
    creative: IGetCreativesDataFormatted;
    offer: IGetOffersDataFormatted;
    handleModalFinish: ({
        creative,
        offer,
    }: {
        creative: IGetCreativesDataFormatted;
        offer: IGetOffersDataFormatted;
    }) => void;
}

const ModalSelectPlacement: React.FC<IModalSelectPlacement> = ({
    onClose,
    offer,
    creative,
    handleModalFinish,
}) => {
    const { t } = useI18n('Creatives');
    const { t: tCommon } = useI18n('Common');
    const { t: tErrors } = useI18n('Errors');

    const [selectedPlacement, setSelectedPlacement] = useState(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const {
        data: placements = [],
        isLoading: placementsIsLoading,
        isReachingEnd,
        nextPage,
        error: placementsError,
    } = usePlacements({
        params: {
            offerIds: offer.id,
            fields: 'id,status,url',
            statuses: 'allow',
            limit: 20,
            offset: 0,
        },
        dependencies: [offer.id],
    }) as ICreateUseRequestInfiniteResponse<IGetPlacementsDataFormatted[]>;

    useEffect(() => {
        if (placementsError) {
            Notification.error({ message: placementsError.message });
        }
    }, [placementsError]);

    const onScrollSelect = debounce(e => {
        infifniteScroll(() => {
            if (!isReachingEnd) {
                nextPage();
            }
        }, e.target);
    }, 300);

    const onChangePlacement = id => {
        setSelectedPlacement(id);
    };

    const onFinish = async () => {
        try {
            setIsLoading(true);
            const res = await api.creative.editCreative({
                urlParams: { id: creative.id },
                body: { placementId: selectedPlacement },
            });
            if (res.result) {
                handleModalFinish({ creative, offer });
            }
        } catch (error) {
            console.error(error);
            Sentry.captureException(error);
            Notification.error({ message: tErrors('Something went wrong') });
        } finally {
            setIsLoading(false);
        }
    };

    const modalIsLoading = placementsIsLoading || isLoading;

    return (
        <Dialog open onCancel={onClose} title={t('Choose a placement to receive token')}>
            <Loader loading={modalIsLoading}>
                <Form onFinish={onFinish}>
                    <Form.Item
                        label={t('Available placements')}
                        name="offer"
                        rules={[{ required: true, message: tCommon('Required field') }]}
                    >
                        <Select
                            loading={placementsIsLoading}
                            options={(placements as unknown) as DefaultOptionType[]}
                            fieldNames={{ label: 'url', value: 'id' }}
                            onChange={onChangePlacement}
                            placeholder={t('Select placement')}
                            className="w-full"
                            onPopupScroll={onScrollSelect}
                        />
                    </Form.Item>
                    <Form.Item className="text-right">
                        <Button htmlType="submit" type="primary" disabled={!selectedPlacement}>
                            {t('Get token')}
                        </Button>
                    </Form.Item>
                </Form>
            </Loader>
        </Dialog>
    );
};

export default ModalSelectPlacement;
